import React, { useState, useEffect, useMemo } from 'react'
import {
  FileText,
  Calculator,
  Building2,
  FileCheck,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Calendar,
  AlertTriangle,
  MapPin,
  LayoutDashboard,
  Layers,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Landmark,
  CalendarRange,
  Activity,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppPath } from '../hooks/useAppPath'
import { useExchange } from '../contexts/ExchangeContext'
import { useDashboardMetrics } from '../hooks/useDashboardMetrics'
import { formatCurrency } from '../utils/exchangeCalculations'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ExchangeWizardHub } from '../components/wizard/ExchangeWizardHub'
import { ExchangeHub } from '../components/wizard/ExchangeHub'
import { PurchaseHub } from '../components/wizard/PurchaseHub'
import { DisbursementHub } from '../components/wizard/DisbursementHub'
import { EscrowHub } from '../components/wizard/EscrowHub'
import { IrsHub } from '../components/wizard/IrsHub'
import { EscrowPanel } from '../components/dashboard/EscrowPanel'
import { MetricCard } from '../components/dashboard/MetricCard'
import { HubCard } from '../components/dashboard/HubCard'
import { propertyApi } from '../services/api/property'
import toast from 'react-hot-toast'
import { useHubAccess } from '../hooks/useHubAccess'
import { KYCVerificationModal } from '../components/auth/KYCVerificationModal'
import { VerifyIdentityBanner } from '../components/dashboard/VerifyIdentityBanner'
import { activityLog } from '../services/activityLog'
import { dataPersistence } from '../services/dataPersistence'
import { useDemoResetSync } from '../hooks/useDemoResetSync'


export const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const seedFlag = searchParams.get('seed')
  const appPath = useAppPath()
  const { user, isIdentityVerified } = useAuth()
  const [isAllActivityModalOpen, setIsAllActivityModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const demoGuidanceToastId = 'demo1-guidance'
  const demoGuidanceStageRef = React.useRef<string>('')
  const demoGuidanceTimeoutRef = React.useRef<number | null>(null)

  const hubSubmitted = (hubType: 'sales' | 'escrow' | 'identification' | 'purchase' | 'disbursement' | 'irs'): boolean => {
    return localStorage.getItem(`${hubType}_hub_submitted`) === 'true'
  }

  const toggleHubLock = (hubType: 'sales' | 'escrow' | 'identification' | 'purchase' | 'disbursement' | 'irs') => {
    if (hubSubmitted(hubType)) return
    const lockKey = `hub_locked_${hubType}`
    const locked = localStorage.getItem(lockKey) === 'true'
    if (locked) {
      localStorage.removeItem(lockKey)
    } else {
      localStorage.setItem(lockKey, 'true')
    }
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
  }

  const isHubLocked = (hubType: 'sales' | 'escrow' | 'identification' | 'purchase' | 'disbursement' | 'irs'): boolean => {
    if (hubSubmitted(hubType)) return true
    return localStorage.getItem(`hub_locked_${hubType}`) === 'true'
  }
  const [isEscrowHubOpen, setIsEscrowHubOpen] = useState(false)
  const [isExchangeHubOpen, setIsExchangeHubOpen] = useState(false)
  const [isPurchaseHubOpen, setIsPurchaseHubOpen] = useState(false)
  const [isDisbursementHubOpen, setIsDisbursementHubOpen] = useState(false)
  const [isIrsHubOpen, setIsIrsHubOpen] = useState(false)
  const [wizardInitialTab, setWizardInitialTab] = useState<'initial' | 'exchange' | 'acquisition'>('initial')
  React.useRef(false)
  const [showKYCModal, setShowKYCModal] = useState(false)
  const [userActivities, setUserActivities] = useState(activityLog.getActivities())
  const [uploadedDocsCount, setUploadedDocsCount] = useState(0)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [propertySaleDate, setPropertySaleDate] = useState<Date | null>(null)
  const [relinquishedAddress, setRelinquishedAddress] = useState<string>('')
  const [_relinquishedCity, setRelinquishedCity] = useState<string>('')
  const [_relinquishedStateCode, setRelinquishedStateCode] = useState<string>('')
  const [_relinquishedZip, setRelinquishedZip] = useState<string>('')
  const [_relinquishedClosingDate, setRelinquishedClosingDate] = useState<string | null>(null)
  const [_relinquishedImage, setRelinquishedImage] = useState<string>('')
  const [_relinquishedPrice, setRelinquishedPrice] = useState<string | null>(null)
  const [_relinquishedBeds, setRelinquishedBeds] = useState<number | null>(null)
  const [_relinquishedBaths, setRelinquishedBaths] = useState<number | null>(null)
  const [_relinquishedSqft, setRelinquishedSqft] = useState<number | null>(null)
  const [_zillowUrl, setZillowUrl] = useState<string | null>(null)
  const [_redfinUrl, setRedfinUrl] = useState<string | null>(null)
  const [hasStartedProcess, setHasStartedProcess] = useState(() => {
    const activities = activityLog.getActivities()
    return activities.some(a => a.type === 'document' && a.title.includes('Sales & Purchase Agreement'))
  })
  const [salesHubPropertySaleStatus, setSalesHubPropertySaleStatus] = useState<string>('')
  const [_hubRefreshTrigger, setHubRefreshTrigger] = useState(0)

  const { currentExchange } = useExchange()

  useDemoResetSync(currentExchange?.id, currentExchange?.demoResetAt, user?.id)

  useEffect(() => {
    if (seedFlag !== 'sample' || !user) return
    import('../lib/demoInit').then(({ seedDemo1Exchange }) => {
      seedDemo1Exchange(String(currentExchange?.id || user.id), String(user.id))
      searchParams.delete('seed')
      setSearchParams(searchParams, { replace: true })
      setTimeout(() => window.location.reload(), 100)
    })
  }, [seedFlag, user, currentExchange?.id, searchParams, setSearchParams])

  // Helper to check if user is a demo user
  const isDemoUser = useMemo(() => {
    if (!user?.email) return false
    const userEmail = String(user.email).toLowerCase().trim()
    return userEmail === 'demo@open1031.ai' || userEmail === 'demo1@open1031.ai'
  }, [user?.email])
  const getSalesHubWizardData = React.useCallback(() => {
    const exchangeId = currentExchange?.id || user?.id || 'anon'
    const keys = [
      `sale_wizard_v1_${exchangeId}_phase1`,
      `sale_wizard_v1_${exchangeId}_initial`,
      `exchange_wizard_v1_${exchangeId}_initial`
    ]
    for (const key of keys) {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      try {
        return JSON.parse(raw)
      } catch {}
    }
    return null
  }, [currentExchange?.id, user?.id])

  const loadSalesHubPropertySaleStatus = React.useCallback(() => {
    const data = getSalesHubWizardData()
    const status = typeof data?.propertySaleStatus === 'string' ? data.propertySaleStatus : ''
    setSalesHubPropertySaleStatus(status)
  }, [getSalesHubWizardData])

  const computeDemoGuidanceStage = React.useCallback((): { stageKey: string; message: string } | null => {
    if (!user?.email || String(user.email).toLowerCase().trim() !== 'demo1@open1031.ai') return null
    if (!isIdentityVerified) {
      return { stageKey: 'kyc', message: 'Step 1: Complete KYC identity verification to unlock the Sales Hub.' }
    }
    const salesHubSubmitted = localStorage.getItem('sales_hub_submitted') === 'true'
    const identificationHubSubmitted = localStorage.getItem('identification_hub_submitted') === 'true'
    const purchaseHubSubmitted = localStorage.getItem('purchase_hub_submitted') === 'true'
    const disbursementHubSubmitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
    const escrowHubSubmitted = localStorage.getItem('escrow_hub_submitted') === 'true'
    if (!salesHubSubmitted) {
      const wizard = getSalesHubWizardData()
      const exchangeInfoComplete = !!wizard?.exchangeType
      if (!exchangeInfoComplete) {
        return { stageKey: 'sales.exchange-info', message: 'Step 2: Sales Hub — select your exchange type and answer the exchange questions.' }
      }
      const spaExtracted = wizard?.hasExtractedWithNeela === true
      const spaUploaded = wizard?.salesPurchaseAgreementUploaded === true
      const titleUploaded = wizard?.preliminaryTitleReportUploaded === true
      const titleExtracted = wizard?.hasTitleReportExtracted === true
      const escrowRequired = wizard?.isInEscrow === true
      if (!spaUploaded) {
        return { stageKey: 'sales.upload-spa', message: 'Step 2: Sales Hub — upload your Sales & Purchase Agreement.' }
      }
      if (escrowRequired && !titleUploaded) {
        return { stageKey: 'sales.upload-title', message: 'Step 2: Sales Hub — upload your Preliminary Title Report (required when in escrow).' }
      }
      if (!spaExtracted) {
        return { stageKey: 'sales.extract-spa', message: 'Step 2: Sales Hub — run Neela extraction on the Sales & Purchase Agreement.' }
      }
      if (titleUploaded && !titleExtracted) {
        return { stageKey: 'sales.extract-title', message: 'Step 2: Sales Hub — run Neela extraction on the Preliminary Title Report.' }
      }
      const exchangers = Array.isArray(wizard?.exchangers) ? wizard.exchangers : []
      const exchangersComplete = exchangers.length > 0 && exchangers.every((e: any) => {
        const hasAddress = !!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))
        return !!(e.firstName && e.lastName && e.email && e.phone && hasAddress && e.taxIdNumber)
      })
      if (!exchangersComplete) {
        return { stageKey: 'sales.exchangers', message: 'Step 2: Sales Hub — confirm exchanger details.' }
      }
      const propertyDetailsComplete = !!(
        wizard?.relinquished?.addressOrLegalDescription &&
        wizard?.buyer?.names &&
        wizard?.closingCompanySale?.name &&
        wizard?.closingCompanySale?.address &&
        wizard?.closingCompanySale?.phone &&
        wizard?.closingCompanySale?.closerName &&
        wizard?.closingCompanySale?.closerEmail
      )
      if (!propertyDetailsComplete) {
        return { stageKey: 'sales.property-details', message: 'Step 2: Sales Hub — confirm property, buyer, and closing details.' }
      }
      const signaturesComplete = localStorage.getItem('exchange_agreement_signed') === 'true' && localStorage.getItem('w9_signed') === 'true'
      if (!signaturesComplete) {
        return { stageKey: 'sales.signatures', message: 'Step 2: Sales Hub — e-sign the Exchange Agreement and W-9.' }
      }
      return { stageKey: 'sales.submit', message: 'Step 2: Sales Hub — submit the Sales Hub section.' }
    }
    if (!identificationHubSubmitted) {
      return { stageKey: 'identification', message: 'Step 3: Identification Hub — your 45-day clock is running. Identify replacement properties now. You can set up Escrow in parallel.' }
    }
    if (!escrowHubSubmitted) {
      return { stageKey: 'escrow', message: 'Step 4: Escrow Hub — acknowledge QI custody to complete escrow setup.' }
    }
    if (!purchaseHubSubmitted) {
      return { stageKey: 'purchase', message: 'Step 5: Purchase Hub — complete and submit the Purchase Hub section.' }
    }
    if (!disbursementHubSubmitted) {
      return { stageKey: 'disbursement', message: 'Step 6: Disbursement Hub — complete and submit the Disbursement Form.' }
    }
    return { stageKey: 'done', message: 'All hubs completed. Your 1031 exchange is ready for final review.' }
  }, [getSalesHubWizardData, isIdentityVerified, user?.email])
  const updateDemoGuidanceAlert = React.useCallback(() => {
    const next = computeDemoGuidanceStage()
    if (!next) return
    const prevStageKey = demoGuidanceStageRef.current
    const isNewStage = prevStageKey !== next.stageKey
    if (!isNewStage) return
    if (demoGuidanceTimeoutRef.current) {
      window.clearTimeout(demoGuidanceTimeoutRef.current)
      demoGuidanceTimeoutRef.current = null
    }
    const showInfo = () => {
      toast(next.message, { id: demoGuidanceToastId, duration: 3500 })
      demoGuidanceStageRef.current = next.stageKey
    }
    const showSuccessThenContinue = (successMessage: string) => {
      toast.success(successMessage, { id: demoGuidanceToastId, duration: 2000 })
      demoGuidanceStageRef.current = next.stageKey
      demoGuidanceTimeoutRef.current = window.setTimeout(() => {
        demoGuidanceStageRef.current = ''
        updateDemoGuidanceAlert()
      }, 2100)
    }
    if (prevStageKey === 'kyc' && next.stageKey.startsWith('sales')) {
      showSuccessThenContinue('KYC completed.')
      return
    }
    if (prevStageKey.startsWith('sales') && next.stageKey === 'identification') {
      showSuccessThenContinue('Sales Hub completed.')
      return
    }
    if (prevStageKey === 'identification' && next.stageKey === 'escrow') {
      showSuccessThenContinue('Identification Hub completed.')
      return
    }
    if (prevStageKey === 'escrow' && next.stageKey === 'purchase') {
      showSuccessThenContinue('Escrow acknowledged.')
      return
    }
    if (prevStageKey === 'purchase' && next.stageKey === 'disbursement') {
      showSuccessThenContinue('Purchase Hub completed.')
      return
    }
    if (prevStageKey === 'disbursement' && next.stageKey === 'done') {
      showSuccessThenContinue('Disbursement Hub completed.')
      return
    }
    showInfo()
  }, [computeDemoGuidanceStage])
  useEffect(() => {
    if (!user?.email || String(user.email).toLowerCase().trim() !== 'demo1@open1031.ai') return
    updateDemoGuidanceAlert()
    const handler = () => updateDemoGuidanceAlert()
    window.addEventListener('salesHubCompleted', handler)
    window.addEventListener('escrowHubCompleted', handler)
    window.addEventListener('identificationHubCompleted', handler)
    window.addEventListener('purchaseHubCompleted', handler)
    window.addEventListener('disbursementHubCompleted', handler)
    return () => {
      window.removeEventListener('salesHubCompleted', handler)
      window.removeEventListener('escrowHubCompleted', handler)
      window.removeEventListener('identificationHubCompleted', handler)
      window.removeEventListener('purchaseHubCompleted', handler)
      window.removeEventListener('disbursementHubCompleted', handler)
      if (demoGuidanceTimeoutRef.current) {
        window.clearTimeout(demoGuidanceTimeoutRef.current)
        demoGuidanceTimeoutRef.current = null
      }
      toast.dismiss(demoGuidanceToastId)
      demoGuidanceStageRef.current = ''
    }
  }, [updateDemoGuidanceAlert, user?.email])

  // Hub access control
  const salesAccess = useHubAccess('sales', user, isIdentityVerified)
  const escrowAccess = useHubAccess('escrow', user, isIdentityVerified)
  const purchaseAccess = useHubAccess('purchase', user, isIdentityVerified)
  const identificationAccess = useHubAccess('identification', user, isIdentityVerified)
  const disbursementAccess = useHubAccess('disbursement', user, isIdentityVerified)
  const irsAccess = useHubAccess('irs', user, isIdentityVerified)

  const getNextStep = (): { hub: string; label: string; action: () => void } | null => {
    if (!isIdentityVerified) {
      return { hub: 'kyc', label: 'Verify Your Identity', action: () => setShowKYCModal(true) }
    }
    if (!salesAccess.isSubmitted) {
      return { hub: 'sales', label: 'Complete Sales Hub', action: () => { setWizardInitialTab('initial'); setIsWizardOpen(true) } }
    }
    if (!escrowAccess.isSubmitted) {
      return { hub: 'escrow', label: 'Acknowledge Escrow', action: () => setIsEscrowHubOpen(true) }
    }
    if (!identificationAccess.isSubmitted) {
      return { hub: 'identification', label: 'Complete Identification Hub', action: () => setIsExchangeHubOpen(true) }
    }
    if (!purchaseAccess.isSubmitted) {
      return { hub: 'purchase', label: 'Complete Purchase Hub', action: () => setIsPurchaseHubOpen(true) }
    }
    if (!disbursementAccess.isSubmitted) {
      return { hub: 'disbursement', label: 'Complete Disbursement Hub', action: () => setIsDisbursementHubOpen(true) }
    }
    if (!irsAccess.isSubmitted) {
      return { hub: 'irs', label: 'Complete IRS Hub', action: () => setIsIrsHubOpen(true) }
    }
    return null
  }

  const nextStep = getNextStep()

  // Update timer every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute
    return () => clearInterval(timer)
  }, [])

  // Listen for activity updates
  useEffect(() => {
    const handleActivityAdded = () => {
      setUserActivities(activityLog.getActivities())
    }
    window.addEventListener('activityAdded', handleActivityAdded)
    return () => window.removeEventListener('activityAdded', handleActivityAdded)
  }, [])

  useEffect(() => {
    const handleSalesHubCompleted = () => {
      setHubRefreshTrigger(prev => prev + 1)
      setUserActivities(activityLog.getActivities())
    }
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted)
    return () => window.removeEventListener('salesHubCompleted', handleSalesHubCompleted)
  }, [])

  useEffect(() => {
    const handleOpenIdHub = () => {
      setIsWizardOpen(false)
      setIsExchangeHubOpen(true)
    }
    const handleOpenEscrowHub = () => {
      setIsWizardOpen(false)
      setIsEscrowHubOpen(true)
    }
    window.addEventListener('openIdentificationHub', handleOpenIdHub)
    window.addEventListener('openEscrowHub', handleOpenEscrowHub)
    return () => {
      window.removeEventListener('openIdentificationHub', handleOpenIdHub)
      window.removeEventListener('openEscrowHub', handleOpenEscrowHub)
    }
  }, [])

  // Listen for escrow hub completion
  useEffect(() => {
    const handleEscrowHubCompleted = () => {
      setHubRefreshTrigger(prev => prev + 1)
    }
    window.addEventListener('escrowHubCompleted', handleEscrowHubCompleted)
    return () => window.removeEventListener('escrowHubCompleted', handleEscrowHubCompleted)
  }, [])

  // Track uploaded documents and update hasStartedProcess when docs are uploaded AND Neela has extracted
  useEffect(() => {
    const checkDocuments = () => {
      // Check Sales Hub wizard data for uploaded documents
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      const key3 = `sale_wizard_v1_${exchangeId}_phase1`
      const raw1 = localStorage.getItem(key1)
      const raw2 = localStorage.getItem(key2)
      const raw3 = localStorage.getItem(key3)
      const raw = raw3 || raw2 || raw1
      
      let hasSalesPurchaseAgreement = false
      let hasPreliminaryTitle = false
      let hasExtractedWithNeela = false
      
      if (raw) {
        try {
          const parsed = JSON.parse(raw)
          hasSalesPurchaseAgreement = parsed.salesPurchaseAgreementUploaded === true
          hasPreliminaryTitle = parsed.preliminaryTitleReportUploaded === true
          // Only update dashboard metrics if Neela has extracted data
          hasExtractedWithNeela = parsed.hasExtractedWithNeela === true
          
          // Check if Sales Hub has meaningful data (user has filled out fields)
          const hasSalesHubData = !!(parsed?.relinquished?.addressOrLegalDescription?.trim() || 
                                    parsed?.exchangerPhaseOne?.names?.trim() ||
                                    parsed?.relinquished?.closingDate?.trim())
          
          // Don't update dashboard metrics unless:
          // 1. Neela has extracted data, AND
          // 2. Sales Hub has meaningful data (user has filled out fields)
          if (!hasExtractedWithNeela || !hasSalesHubData) {
            return
          }
        } catch {}
      } else {
        // No wizard data - don't update metrics
        return
      }
      
      // Fallback to activity log if wizard data not available
      if (!hasSalesPurchaseAgreement) {
        hasSalesPurchaseAgreement = userActivities.some(
          activity => activity.type === 'document' && activity.title.includes('Sales & Purchase Agreement')
        )
      }
      if (!hasPreliminaryTitle) {
        hasPreliminaryTitle = userActivities.some(
          activity => activity.type === 'document' && activity.title.includes('Preliminary Title Report')
        )
      }
      
      let count = 0
      if (hasSalesPurchaseAgreement) count++
      if (hasPreliminaryTitle) count++
      
      setUploadedDocsCount(count)
      
      // If user has uploaded the Sales & Purchase Agreement AND Neela has extracted, mark process as started
      if (hasSalesPurchaseAgreement && !hasStartedProcess) {
        setHasStartedProcess(true)
      }
      
      // Set sale date - use future date if property is in escrow and both documents uploaded, otherwise today
      // Only set if Neela has extracted data
      if (hasSalesPurchaseAgreement && !propertySaleDate) {
        // Check if property is in escrow from wizard data
        const exchangeId = currentExchange?.id || user?.id || 'anon'
        const key1 = `exchange_wizard_v1_${exchangeId}_initial`
        const key2 = `sale_wizard_v1_${exchangeId}_initial`
        const raw1 = localStorage.getItem(key1)
        const raw2 = localStorage.getItem(key2)
        const raw = raw2 || raw1
        
        let isInEscrow = false
        if (raw) {
          try {
            const parsed = JSON.parse(raw)
            isInEscrow = parsed.isInEscrow === true
          } catch {}
        }
        
        // If property is in escrow and both documents are uploaded, use future date (30 days from now)
        const saleDate = (isInEscrow && hasSalesPurchaseAgreement && hasPreliminaryTitle)
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days from now
          : new Date() // Today
        
        setPropertySaleDate(saleDate)
        setRelinquishedClosingDate(saleDate.toISOString().split('T')[0])
        localStorage.setItem('property_sale_date', saleDate.toISOString())
      }
    }
    
    checkDocuments()
    
    // Listen for wizard data updates and activity additions
    const handleWizardUpdate = () => {
      checkDocuments()
      loadRelinquishedPropertyData()
      // Force a re-render by updating currentTime to trigger recalculation
      setCurrentTime(new Date())
    }
    
    const handleActivityAdded = () => {
      checkDocuments()
    }
    
    const handleHubLockUpdate = () => {
      setHubRefreshTrigger(prev => prev + 1)
    }
    
    window.addEventListener('wizardDataUpdated', handleWizardUpdate)
    window.addEventListener('activityAdded', handleActivityAdded)
    window.addEventListener('wizardDataUpdated', handleHubLockUpdate)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleWizardUpdate)
      window.removeEventListener('activityAdded', handleActivityAdded)
      window.removeEventListener('wizardDataUpdated', handleHubLockUpdate)
    }
  }, [userActivities, hasStartedProcess, propertySaleDate, currentExchange?.id, user?.id])

  useEffect(() => {
    if (isAllActivityModalOpen) {
      setCurrentPage(1)
    }
  }, [isAllActivityModalOpen])

  // Load calculator savings and property sale date from localStorage
  useEffect(() => {
    // Load property sale date from localStorage if previously set
    // Only load if it exists AND wizard data confirms it should be set
    const savedSaleDate = localStorage.getItem('property_sale_date')
    if (savedSaleDate) {
      try {
        const date = new Date(savedSaleDate)
        if (!isNaN(date.getTime())) {
          // Verify that wizard data exists and has meaningful data before setting sale date
          const exchangeId = currentExchange?.id || user?.id || 'anon'
          const key1 = `exchange_wizard_v1_${exchangeId}_initial`
          const key2 = `sale_wizard_v1_${exchangeId}_initial`
          const raw1 = localStorage.getItem(key1)
          const raw2 = localStorage.getItem(key2)
          const raw = raw2 || raw1
          
          if (raw) {
            try {
              const parsed = JSON.parse(raw)
              const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address
              const hasData = (addressOrDesc && addressOrDesc.trim() !== '') || 
                             (parsed?.exchangerPhaseOne?.names && parsed.exchangerPhaseOne.names.trim() !== '') ||
                             (parsed?.relinquished?.closingDate && parsed.relinquished.closingDate.trim() !== '')
              
              if (hasData) {
                setPropertySaleDate(date)
                setRelinquishedClosingDate(date.toISOString().split('T')[0])
              } else {
                // No meaningful data, clear the sale date
                setPropertySaleDate(null)
                setRelinquishedClosingDate(null)
                localStorage.removeItem('property_sale_date')
              }
            } catch {
              // If parsing fails, don't set the date
              setPropertySaleDate(null)
              setRelinquishedClosingDate(null)
            }
          } else {
            // No wizard data, clear the sale date
            setPropertySaleDate(null)
            setRelinquishedClosingDate(null)
            localStorage.removeItem('property_sale_date')
          }
        }
      } catch {
        setPropertySaleDate(null)
        setRelinquishedClosingDate(null)
      }
    }
  }, [currentExchange?.id, user?.id])

  // Seed mock exchange data for demo@open1031.ai user
  useEffect(() => {
    if (!user) return
    
    // Handle demo1 user - auto-seed the cohesive Schechtman-style end-to-end exchange
    if (user.email === 'demo1@open1031.ai') {
      const exchangeId = currentExchange?.id || user.id || 'demo1'
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      const existingData1 = localStorage.getItem(key1)
      const existingData2 = localStorage.getItem(key2)

      if (!existingData1 && !existingData2) {
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(String(exchangeId))
          window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        })
      }
      
      // Ensure demo data persistence is set up
      if (user.firstName && user.lastName) {
        dataPersistence.merge({
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone || '',
          address: user.address || {
            street: '1234 Maple Avenue',
            city: 'Los Angeles',
            state: 'CA',
            zipCode: '90001'
          }
        })
      }
      
      return
    }
    
    // Handle fresh demo user - initialize empty state
    if (user.email === 'demofresh@open1031.ai') {
      const exchangeId = currentExchange?.id || user.id || 'demo-fresh'
      const key = `exchange_wizard_v1_${exchangeId}_initial`
      const existingData = localStorage.getItem(key)
      
      if (!existingData) {
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(String(user.id))
        })
      }
      localStorage.removeItem('exchange_agreement_signed')
      localStorage.removeItem('exchange_agreement_data')
      localStorage.removeItem('exchange_agreement_signed_date')
      localStorage.removeItem('exchange_agreement_signed_by')
      return
    }
    
    if (user.email === 'demo1@open1031.ai') {
      const wasJustReset = localStorage.getItem('demo_reset_just_completed') === 'true'
      if (wasJustReset) {
        localStorage.removeItem('demo_reset_just_completed')
        const exchangeId = currentExchange?.id || String(user.id)
        import('../lib/demoInit').then(({ initializeDemoUser }) => {
          initializeDemoUser(exchangeId)
          window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        })
      }
    }
    
    // Don't seed mock Sales Hub data - data should only populate after Neela extraction
    // Only profile data should be prefilled (handled in SaleWizard component)
  }, [user, currentExchange?.id])

  // Auto-open Sales Hub for demo1 user after identity verification on login
  useEffect(() => {
    if (!user || user.email !== 'demo1@open1031.ai') return
    if (!isIdentityVerified) return
    
    const hasShownSalesHubKey = `sales_hub_shown_demo1_session`
    const hasShownSalesHub = sessionStorage.getItem(hasShownSalesHubKey) === 'true'
    
    if (!hasShownSalesHub && !isWizardOpen) {
      sessionStorage.setItem(hasShownSalesHubKey, 'true')
      setTimeout(() => {
        setIsWizardOpen(true)
        setWizardInitialTab('initial')
      }, 500)
    }
  }, [user, isIdentityVerified, isWizardOpen])


  const loadRelinquishedPropertyData = React.useCallback(() => {
    try {
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      // Check all storage keys for compatibility (phase1 takes priority as it's the most recent)
      const key1 = `exchange_wizard_v1_${exchangeId}_initial`
      const key2 = `sale_wizard_v1_${exchangeId}_initial`
      const key3 = `sale_wizard_v1_${exchangeId}_phase1`
      const raw1 = localStorage.getItem(key1)
      const raw2 = localStorage.getItem(key2)
      const raw3 = localStorage.getItem(key3)
      const raw = raw3 || raw2 || raw1
      
      if (raw) {
        const parsed = JSON.parse(raw)
        // Only load data if Neela has extracted it (hasExtractedWithNeela must be true)
        const hasExtractedWithNeela = parsed?.hasExtractedWithNeela === true
        
        // Check if Sales Hub has meaningful data (user has filled out fields)
        const hasSalesHubData = !!(parsed?.relinquished?.addressOrLegalDescription?.trim() || 
                                  parsed?.exchangerPhaseOne?.names?.trim() ||
                                  parsed?.relinquished?.closingDate?.trim())
        
        if (!hasExtractedWithNeela || !hasSalesHubData) {
          // Don't load Sales Hub data until Neela has extracted it AND Sales Hub has data
          return
        }
        
        // Only load data if user has actually started the process
        // Check for non-empty values (not just truthy, since empty strings are falsy)
        const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address || parsed?.relinquishedProperty?.addressOrLegalDescription
        const exchangerNames = parsed?.exchangerPhaseOne?.names || parsed?.exchangers?.[0]?.names
        const closingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
        const hasData = (addressOrDesc && addressOrDesc.trim() !== '') || 
                       (exchangerNames && exchangerNames.trim() !== '') || 
                       (closingDate && closingDate.trim() !== '')
        
        if (hasData) {
          setHasStartedProcess(true)
          
          // Parse address from addressOrLegalDescription format: "123 Street, City, State ZIP"
          if (addressOrDesc) {
            const addressParts = addressOrDesc.split(',').map((p: string) => p.trim())
            if (addressParts.length >= 3) {
              setRelinquishedAddress(addressParts[0])
              setRelinquishedCity(addressParts[1])
              const stateZip = addressParts[2].split(' ')
              if (stateZip.length >= 2) {
                setRelinquishedStateCode(stateZip[0])
                setRelinquishedZip(stateZip.slice(1).join(' '))
              }
            } else if (addressParts.length === 1) {
              setRelinquishedAddress(addressParts[0])
            }
          } else {
            // Fallback to old format
            const rel = parsed?.relinquished || parsed?.relinquishedProperty
            if (rel?.address) setRelinquishedAddress(rel.address)
            if (rel?.city) setRelinquishedCity(rel.city)
            if (rel?.state) setRelinquishedStateCode(rel.state)
            if (rel?.zip) setRelinquishedZip(rel.zip)
          }
          
          // Check for closing date in both formats (relinquished and relinquishedProperty)
          const finalClosingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
          if (finalClosingDate && finalClosingDate.trim() !== '') {
            setRelinquishedClosingDate(finalClosingDate)
            const isDemo1 = user?.email === 'demo1@open1031.ai'
            const hasSalesHubStarted = !!(addressOrDesc || exchangerNames)
            const closingDate = new Date(finalClosingDate)
            if (!isNaN(closingDate.getTime())) {
              if (!isDemo1 || hasSalesHubStarted) {
                setPropertySaleDate(closingDate)
                localStorage.setItem('property_sale_date', closingDate.toISOString())
              } else if (isDemo1) {
                setPropertySaleDate(null)
              }
            }
          } else {
            const storedSaleDate = localStorage.getItem('propertySaleDate')
            if (storedSaleDate) {
              const parsedSaleDate = new Date(storedSaleDate)
              if (!isNaN(parsedSaleDate.getTime())) {
                setPropertySaleDate(parsedSaleDate)
              }
            } else if (currentExchange?.saleCloseDate) {
              const backendDate = new Date(currentExchange.saleCloseDate)
              if (!isNaN(backendDate.getTime())) {
                setPropertySaleDate(backendDate)
              }
            }
          }
          
          // For demo user, set mock property details
          if (isDemoUser && (addressOrDesc?.includes('123 Oak') || parsed?.relinquished?.address === '123 Oak Street')) {
            setRelinquishedImage('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80&auto=format&fit=crop')
            setRelinquishedPrice('$1,048,000')
            setRelinquishedBeds(3)
            setRelinquishedBaths(3)
            setRelinquishedSqft(1628)
            setZillowUrl('https://www.zillow.com/homes/123-Oak-Street-Los-Angeles-CA-90001')
            setRedfinUrl('https://www.redfin.com/stingray/do/location-autocomplete?location=123%20Oak%20Street%2C%20Los%20Angeles%2C%20CA%2090001')
          }
        } else {
          // No meaningful data found - reset to not started
          setHasStartedProcess(false)
          setPropertySaleDate(null)
          setRelinquishedClosingDate(null)
          setRelinquishedAddress('')
          setRelinquishedCity('')
          setRelinquishedStateCode('')
          setRelinquishedZip('')
          // For demo1: ensure propertySaleDate is cleared when no data exists
          if (user?.email === 'demo1@open1031.ai') {
            setPropertySaleDate(null)
          }
        }
      } else {
        // No wizard data at all - reset to not started
        setHasStartedProcess(false)
        setPropertySaleDate(null)
        setRelinquishedClosingDate(null)
        setRelinquishedAddress('')
        setRelinquishedCity('')
        setRelinquishedStateCode('')
        setRelinquishedZip('')
        // For demo1: ensure propertySaleDate is cleared when no wizard data exists
        if (user?.email === 'demo1@open1031.ai') {
          setPropertySaleDate(null)
        }
      }
    } catch (error) {
      console.error('Failed to load property data:', error)
      // On error, reset to not started
      setHasStartedProcess(false)
      setPropertySaleDate(null)
    }
  }, [currentExchange?.id, currentExchange?.saleCloseDate, user?.id, user?.email])

  useEffect(() => {
    loadRelinquishedPropertyData()
  }, [loadRelinquishedPropertyData])

  useEffect(() => {
    if (!currentExchange?.saleCloseDate) return
    const date = new Date(currentExchange.saleCloseDate)
    if (isNaN(date.getTime())) return
    setPropertySaleDate(date)
  }, [currentExchange?.saleCloseDate])

  useEffect(() => {
    loadSalesHubPropertySaleStatus()
  }, [loadSalesHubPropertySaleStatus])

  useEffect(() => {
    const handler = () => {
      loadRelinquishedPropertyData()
      loadSalesHubPropertySaleStatus()
      setHubRefreshTrigger(prev => prev + 1)
    }
    window.addEventListener('salesHubCompleted', handler)
    window.addEventListener('wizardDataUpdated', handler)
    return () => {
      window.removeEventListener('salesHubCompleted', handler)
      window.removeEventListener('wizardDataUpdated', handler)
    }
  }, [loadRelinquishedPropertyData, loadSalesHubPropertySaleStatus])

  // Listen for wizard data updates
  useEffect(() => {
    const handleDataUpdate = (event: CustomEvent) => {
      if (event.detail?.scope === 'initial') {
        loadRelinquishedPropertyData()
      }
    }
    
    window.addEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleDataUpdate as EventListener)
    }
  }, [loadRelinquishedPropertyData])

  // Fetch from property API when address changes (skip for demo user with mock data)
  useEffect(() => {
    // Skip API call for demo user with mock property
    if (isDemoUser && relinquishedAddress === '123 Oak Street') {
      return
    }
    
    let cancelled = false
    const run = async () => {
      if (!relinquishedAddress) return
      try {
        const data = await propertyApi.lookupByAddress(relinquishedAddress)
        if (cancelled) return
        setRelinquishedImage(data.imageUrl)
        setRelinquishedPrice(typeof data.price === 'number' ? `$${data.price.toLocaleString()}` : (data.price || null))
        setRelinquishedBeds(data.beds)
        setRelinquishedBaths(data.baths)
        setRelinquishedSqft(data.sqft)
        setZillowUrl(data.zillowUrl)
        setRedfinUrl(data.redfinUrl)
      } catch (e) {
        // leave defaults
      }
    }
    run()
    return () => { cancelled = true }
  }, [relinquishedAddress, user?.email])

  const identificationDeadline = (() => {
    if (currentExchange?.identificationDeadline) {
      return new Date(currentExchange.identificationDeadline)
    }
    if (!propertySaleDate) return null
    const d = new Date(propertySaleDate)
    d.setDate(d.getDate() + 45)
    return d
  })()

  const exchangeDeadline = (() => {
    if (currentExchange?.exchangeDeadline) {
      return new Date(currentExchange.exchangeDeadline)
    }
    if (!propertySaleDate) return null
    const d = new Date(propertySaleDate)
    d.setDate(d.getDate() + 180)
    return d
  })()
  
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const effectiveSaleDateForDisplay = currentExchange?.saleCloseDate
    ? new Date(currentExchange.saleCloseDate)
    : propertySaleDate

  const { isPropertySold, daysUntilSale, idDaysRemaining, exchangeDaysRemaining, totalDaysElapsed } = useMemo(() => {
    const backendSaleDate = currentExchange?.saleCloseDate ? new Date(currentExchange.saleCloseDate) : null
    const effectiveSaleDate = backendSaleDate ?? propertySaleDate
    const backendIdDeadline = currentExchange?.identificationDeadline ? new Date(currentExchange.identificationDeadline) : null
    const backendExchangeDeadline = currentExchange?.exchangeDeadline ? new Date(currentExchange.exchangeDeadline) : null
    const hasBackendDates = backendSaleDate !== null

    const exchangeId = currentExchange?.id || user?.id || 'anon'
    const key1 = `exchange_wizard_v1_${exchangeId}_initial`
    const key2 = `sale_wizard_v1_${exchangeId}_initial`
    const key3 = `sale_wizard_v1_${exchangeId}_phase1`
    const raw1 = localStorage.getItem(key1)
    const raw2 = localStorage.getItem(key2)
    const raw3 = localStorage.getItem(key3)
    const raw = raw3 || raw2 || raw1
    let hasMeaningfulWizardData = hasBackendDates
    if (!hasBackendDates && raw) {
      try {
        const parsed = JSON.parse(raw)
        const addressOrDesc = parsed?.relinquished?.addressOrLegalDescription || parsed?.relinquished?.address || parsed?.relinquishedProperty?.addressOrLegalDescription
        const exchangerNames = parsed?.exchangerPhaseOne?.names || parsed?.exchangers?.[0]?.names
        const closingDate = parsed?.relinquished?.closingDate || parsed?.relinquishedProperty?.closingDate
        hasMeaningfulWizardData = (addressOrDesc && addressOrDesc.trim() !== '') ||
                                 (exchangerNames && exchangerNames.trim() !== '') ||
                                 (parsed?.buyer?.names && parsed.buyer.names.trim() !== '') ||
                                 (parsed?.closingCompanySale?.name && parsed.closingCompanySale.name.trim() !== '') ||
                                 (closingDate && closingDate.trim() !== '')
      } catch {}
    }

    const totalDaysFromSale = effectiveSaleDate && hasMeaningfulWizardData ? Math.floor((currentTime.getTime() - effectiveSaleDate.getTime()) / (1000 * 60 * 60 * 24)) : null
    const isPropertySold = totalDaysFromSale !== null && totalDaysFromSale >= 0
    const daysUntilSale = effectiveSaleDate && hasMeaningfulWizardData && totalDaysFromSale !== null ? Math.abs(Math.min(0, totalDaysFromSale as number)) : 0
    const idDaysRemaining = backendIdDeadline
      ? Math.max(0, Math.floor((backendIdDeadline.getTime() - currentTime.getTime()) / (1000 * 60 * 60 * 24)))
      : (effectiveSaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null ? Math.max(0, 45 - totalDaysFromSale) : 0)
    const isDemo1 = user?.email === 'demo1@open1031.ai'
    const exchangeDaysRemaining = backendExchangeDeadline
      ? Math.max(0, Math.floor((backendExchangeDeadline.getTime() - currentTime.getTime()) / (1000 * 60 * 60 * 24)))
      : (effectiveSaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null
        ? Math.max(0, 180 - totalDaysFromSale)
        : (isDemo1 ? null : 0))
    const totalDaysElapsed = effectiveSaleDate && hasMeaningfulWizardData && isPropertySold && totalDaysFromSale !== null ? Math.min(180, totalDaysFromSale) : 0

    return { hasMeaningfulWizardData, totalDaysFromSale, isPropertySold, daysUntilSale, idDaysRemaining, exchangeDaysRemaining, totalDaysElapsed }
  }, [propertySaleDate, currentTime, currentExchange?.id, currentExchange?.saleCloseDate, currentExchange?.identificationDeadline, currentExchange?.exchangeDeadline, user?.id, user?.email])
  // Format timestamp helper
  const formatTimestamp = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Recent activities - use real activities from activityLog, or mock data for demo user
  const getIconForActivityType = (type: string) => {
    switch (type) {
      case 'document': return FileCheck
      case 'property': return Building2
      case 'verification': return CheckCircle
      case 'hub': return LayoutDashboard
      case 'form': return FileText
      default: return CheckCircle
    }
  }
  
  const allActivities = (user?.email === 'demo@open1031.ai' && userActivities.length === 0 && hasStartedProcess) ? [
    {
      id: '1',
      type: 'document',
      title: 'Exchange Agreement Signed',
      description: 'You signed the exchange agreement',
      timestamp: formatTimestamp(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: FileCheck
    },
    {
      id: '2',
      type: 'property',
      title: 'Relinquished Property Added',
      description: '123 Oak Street, Los Angeles, CA 90001',
      timestamp: formatTimestamp(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: Building2
    },
    {
      id: '3',
      type: 'verification',
      title: 'Identity Verified',
      description: 'Your identity verification was completed',
      timestamp: formatTimestamp(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: CheckCircle
    },
    {
      id: '4',
      type: 'property',
      title: 'Replacement Property Identified',
      description: '456 Pine Avenue, Los Angeles, CA 90002',
      timestamp: formatTimestamp(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)),
      status: 'completed' as const,
      icon: MapPin
    }
  ] : userActivities.map(activity => ({
    id: activity.id,
    type: activity.type,
    title: activity.title,
    description: activity.description,
    timestamp: formatTimestamp(new Date(activity.timestamp)),
    status: activity.status,
    icon: getIconForActivityType(activity.type)
  }))

  const recentActivities = allActivities.slice(0, 10)

  const totalPages = Math.ceil(allActivities.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedActivities = allActivities.slice(startIndex, endIndex)

  const hubBasedProgress = React.useMemo(() => {
    if (!isIdentityVerified) return 0
    const total =
      salesAccess.completionPercent +
      escrowAccess.completionPercent +
      identificationAccess.completionPercent +
      purchaseAccess.completionPercent +
      disbursementAccess.completionPercent
    return Math.round(total / 5)
  }, [
    isIdentityVerified,
    salesAccess.completionPercent,
    escrowAccess.completionPercent,
    identificationAccess.completionPercent,
    purchaseAccess.completionPercent,
    disbursementAccess.completionPercent,
  ])
  
  const timelineProgressPercent = isIdentityVerified ? hubBasedProgress : 0

  const PROGRESS_STATUS_STYLES: Record<'not-started' | 'in-progress' | 'on-track' | 'complete', string> = {
    'not-started': 'text-muted-foreground',
    'in-progress': 'text-blue-600 dark:text-blue-400',
    'on-track': 'text-green-600 dark:text-green-400',
    'complete': 'text-emerald-600 dark:text-emerald-400',
  }

  const overallProgressStatus: 'not-started' | 'in-progress' | 'on-track' | 'complete' =
    timelineProgressPercent >= 100
      ? 'complete'
      : timelineProgressPercent > 50
      ? 'on-track'
      : timelineProgressPercent > 0
      ? 'in-progress'
      : 'not-started'

  const timelineStatusLabel = (() => {
    if (!isIdentityVerified) return 'Not Started'
    if (isPropertySold && totalDaysElapsed > 0) return `Day ${totalDaysElapsed} of 180`
    if (identificationDeadline && idDaysRemaining > 0) return `${idDaysRemaining} days to identify`
    if (exchangeDeadline && exchangeDaysRemaining !== null && exchangeDaysRemaining > 0) {
      return `${exchangeDaysRemaining} days to close`
    }
    if (effectiveSaleDateForDisplay && daysUntilSale > 0) return `${daysUntilSale} days until start`
    if (timelineProgressPercent > 0) return 'In Progress'
    return 'Not Started'
  })()

  const dashboardMetrics = useDashboardMetrics(
    user?.id ? String(user.id) : undefined,
    hubBasedProgress,
    salesAccess.isComplete,
    uploadedDocsCount
  )

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <LayoutDashboard className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              Welcome, {user?.firstName}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here's an overview of your 1031 exchange progress
            </p>
          </div>
        </div>
      </div>

      {user && !isIdentityVerified && (
        <VerifyIdentityBanner onVerifyClick={() => setShowKYCModal(true)} />
      )}

      {isIdentityVerified && nextStep && nextStep.hub !== 'kyc' && (
        <Card className="p-6 border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-primary/10 dark:from-green-900/20 dark:to-primary/20">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Identity Verified
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                  Great! Your identity has been verified. Ready to continue?
                </p>
              </div>
            </div>
            <Button
              onClick={nextStep.action}
              className="bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-600 text-primary-foreground shadow-lg border-0 px-6 py-3 text-base font-medium w-full md:w-auto"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Next Step: {nextStep.label}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {isIdentityVerified && salesAccess.isSubmitted && escrowAccess.isSubmitted && identificationAccess.isSubmitted && purchaseAccess.isSubmitted && disbursementAccess.isSubmitted && (
        <Card className="p-4 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                All Hubs Complete
              </h3>
              <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                Congratulations! You've completed all required exchange hubs.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Exchange Metrics — always visible, 4 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button
          type="button"
          aria-label={dashboardMetrics.escrowBalance.locked ? 'Escrow Locked — verify identity' : 'Escrow Balance'}
          onClick={() => {
            if (dashboardMetrics.escrowBalance.locked) {
              setShowKYCModal(true)
            }
          }}
          className="text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl"
        >
          {(() => {
            const hubsCompletedCount =
              (salesAccess.isSubmitted ? 1 : 0) +
              (escrowAccess.isSubmitted ? 1 : 0) +
              (identificationAccess.isSubmitted ? 1 : 0) +
              (purchaseAccess.isSubmitted ? 1 : 0) +
              (disbursementAccess.isSubmitted ? 1 : 0)
            const allHubsDone = hubsCompletedCount === 5
            return (
              <MetricCard
                icon={
                  allHubsDone
                    ? <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    : <LayoutDashboard className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                }
                iconBg="bg-emerald-100 dark:bg-emerald-900/30"
                label="Hubs Completed"
                value={`${hubsCompletedCount} of 5`}
                subtitle={
                  allHubsDone
                    ? 'Exchange complete'
                    : hubsCompletedCount === 0
                    ? 'Start with Sales Hub'
                    : 'Through the 1031 journey'
                }
                className="hover:shadow-lg transition-shadow"
              />
            )
          })()}
        </button>
        <button
          type="button"
          aria-label="Documents — view documents page"
          onClick={() => navigate(appPath('/documents'))}
          className="text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl"
        >
          <MetricCard
            icon={<FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />}
            iconBg="bg-purple-100 dark:bg-purple-900/30"
            label="Documents"
            value={`${dashboardMetrics.documents.count}`}
            subtitle="Uploaded & signed"
            className="hover:shadow-lg transition-shadow"
          />
        </button>
        <button
          type="button"
          aria-label="Tax Liability Without 1031 — open calculator"
          onClick={() => navigate(appPath('/calculator'))}
          className="text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl"
        >
          <MetricCard
            icon={<AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />}
            iconBg="bg-red-100 dark:bg-red-900/30"
            label="Tax Liability"
            value={
              dashboardMetrics.taxLiabilityWithout1031.cents !== null
                ? formatCurrency(dashboardMetrics.taxLiabilityWithout1031.cents / 100)
                : '—'
            }
            subtitle={
              dashboardMetrics.taxLiabilityWithout1031.cents !== null
                ? 'Without 1031 exchange'
                : 'Calculate to see'
            }
            className="hover:shadow-lg transition-shadow"
          />
        </button>
        <button
          type="button"
          aria-label="Reinvestment Available — open marketplace"
          onClick={() => navigate(appPath('/marketplace'))}
          className="text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-xl"
        >
          <MetricCard
            icon={<Wallet className="h-4 w-4 text-green-600 dark:text-green-400" />}
            iconBg="bg-green-100 dark:bg-green-900/30"
            label="Reinvestment Available"
            value={
              dashboardMetrics.reinvestmentAvailable.cents !== null
                ? formatCurrency(dashboardMetrics.reinvestmentAvailable.cents / 100)
                : '—'
            }
            subtitle={
              dashboardMetrics.reinvestmentAvailable.phase === 'pre-data'
                ? 'Enter your sale details'
                : dashboardMetrics.reinvestmentAvailable.phase === 'estimated'
                ? 'Estimated proceeds available'
                : 'Tax-free buying power'
            }
            className="hover:shadow-lg transition-shadow"
          />
        </button>
      </div>

      {/* QI Escrow Account — shown after Sales Hub is complete */}
      {salesAccess.isComplete && <EscrowPanel />}

      {/* Exchange Timeline */}
      <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              1031 Exchange Timeline
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Track your progress through the 180-day exchange period
            </p>
          </div>

          {/* Unified Modern Timeline */}
          <div className="relative pt-8 pb-8">
            {/* Overall Progress Info */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-foreground">
                  Overall Exchange Progress
                </span>
                <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                  {timelineProgressPercent}%
                </span>
              </div>
              <span className={`text-sm font-semibold ${PROGRESS_STATUS_STYLES[overallProgressStatus]}`}>
                {timelineStatusLabel}
              </span>
            </div>

            {/* Timeline Track */}
            <div className="absolute top-20 left-0 right-0 h-1.5 bg-border/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 dark:from-green-400 dark:via-blue-400 dark:to-purple-400 transition-all duration-1000 ease-out rounded-full shadow-lg"
                style={{ width: `${timelineProgressPercent}%` }}
              />
            </div>

              {/* Timeline Nodes and Cards */}
              <div className="relative grid grid-cols-3 gap-8 items-start">
                {/* Phase 1: Property Sale + Sales Hub */}
                <div className="flex flex-col items-center w-full">
                  {/* Timeline Node */}
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-muted shadow-lg ring-4 ring-muted/30'
                        : salesAccess.isComplete
                        ? 'bg-green-500 dark:bg-green-600 shadow-2xl shadow-green-500/50 dark:shadow-green-500/30 ring-4 ring-green-200 dark:ring-green-900/50'
                        : 'bg-green-500 dark:bg-green-600 shadow-2xl shadow-green-500/50 dark:shadow-green-500/30 ring-4 ring-green-200 dark:ring-green-900/50 animate-pulse-slow'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <CheckCircle className={`h-7 w-7 ${isIdentityVerified ? 'text-white' : 'text-muted-foreground'}`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      isIdentityVerified ? 'bg-green-500 dark:bg-green-600' : 'bg-muted-foreground/40'
                    }`}>
                      <span className="text-xs font-black text-white">1</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="text-center mb-6">
                    <p className="text-sm font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-1">Phase 1</p>
                    <p className="text-xs text-muted-foreground">Initial Setup</p>
                  </div>
                  
                  {/* Card */}
                  <div className="relative w-full py-3 rounded-xl bg-card border-2 border-green-200/50 dark:border-green-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-foreground mb-0.5">
                        Sell Relinquished Property
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        {!isIdentityVerified || !salesAccess.canAccess || !salesHubPropertySaleStatus
                          ? 'Not started'
                          : isPropertySold
                          ? 'Property sold'
                          : daysUntilSale > 0
                          ? 'Property under contract'
                          : 'In progress'}
                      </p>
                      {isIdentityVerified && salesHubPropertySaleStatus && effectiveSaleDateForDisplay && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-muted-foreground mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(effectiveSaleDateForDisplay)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      ) : !salesAccess.canAccess ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      ) : !salesHubPropertySaleStatus ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      ) : effectiveSaleDateForDisplay ? (
                        daysUntilSale > 0 ? (
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                            Under Contract
                          </div>
                        ) : (
                          <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold">
                            <CheckCircle className="h-2.5 w-2.5 mr-1" />
                            Sold
                          </div>
                        )
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                          <Clock className="h-2.5 w-2.5 mr-1" />
                          In Progress
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phase 2: Identification */}
                <div className="flex flex-col items-center w-full">
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-muted shadow-lg ring-4 ring-muted/30'
                        : identificationAccess.isComplete
                        ? 'bg-blue-500 dark:bg-blue-600 shadow-2xl shadow-blue-500/50 dark:shadow-blue-500/30 ring-4 ring-blue-200 dark:ring-blue-900/50'
                        : salesAccess.isComplete
                        ? 'bg-blue-500 dark:bg-blue-600 shadow-2xl shadow-blue-500/50 dark:shadow-blue-500/30 ring-4 ring-blue-200 dark:ring-blue-900/50 animate-pulse-slow'
                        : 'bg-blue-200 dark:bg-blue-800/50 shadow-lg ring-4 ring-blue-100 dark:ring-blue-900/30'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <Clock className={`h-7 w-7 ${
                          !isIdentityVerified
                            ? 'text-muted-foreground'
                            : (identificationAccess.isComplete || salesAccess.isComplete ? 'text-white' : 'text-blue-600 dark:text-blue-300')
                        }`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      !isIdentityVerified
                        ? 'bg-muted-foreground/40'
                        : identificationAccess.isComplete
                        ? 'bg-blue-500 dark:bg-blue-600'
                        : salesAccess.isComplete
                        ? 'bg-blue-500 dark:bg-blue-600'
                        : 'bg-blue-300 dark:bg-blue-700'
                    }`}>
                      <span className="text-xs font-black text-white">2</span>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <p className={`text-sm font-bold uppercase tracking-wider mb-1 ${
                      identificationAccess.isComplete || salesAccess.isComplete
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-blue-400 dark:text-blue-500'
                    }`}>Phase 2</p>
                    <p className="text-xs text-muted-foreground">During Exchange</p>
                  </div>

                  <div className="relative w-full py-3 rounded-xl bg-card border-2 border-blue-200/50 dark:border-blue-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-foreground mb-0.5">
                        45-Day Identification
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        Identify replacement property
                      </p>
                      {salesAccess.isComplete && identificationDeadline && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-muted-foreground mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(identificationDeadline)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      ) : identificationAccess.isSubmitted ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                          <CheckCircle className="h-2.5 w-2.5 mr-1" />
                          Completed
                        </div>
                      ) : salesAccess.isComplete && identificationDeadline && idDaysRemaining > 0 ? (
                        <div className="inline-flex flex-col items-center px-2 py-1.5 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/70 dark:to-blue-800/70 border-2 border-blue-300 dark:border-blue-600 shadow-md">
                          <div className="text-2xl font-black leading-none text-blue-700 dark:text-blue-100 tracking-tight">
                            {idDaysRemaining}
                          </div>
                          <div className="text-xs font-bold mt-0.5 text-blue-800 dark:text-blue-200 uppercase tracking-wider">
                            days to identify
                          </div>
                        </div>
                      ) : identificationAccess.canAccess && identificationAccess.completionPercent > 0 ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                          <Clock className="h-2.5 w-2.5 mr-1" />
                          In Progress
                        </div>
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Phase 3: 180-Day Exchange + Purchase/Disbursement/IRS Hubs */}
                <div className="flex flex-col items-center w-full">
                  {/* Timeline Node */}
                  <div className="relative z-20 mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                      !isIdentityVerified
                        ? 'bg-muted shadow-lg ring-4 ring-muted/30'
                        : hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'bg-purple-500 dark:bg-purple-600 shadow-2xl shadow-purple-500/50 dark:shadow-purple-500/30 ring-4 ring-purple-200 dark:ring-purple-900/50 animate-pulse-slow'
                        : 'bg-purple-200 dark:bg-purple-800/50 shadow-lg ring-4 ring-purple-100 dark:ring-purple-900/30'
                    }`}>
                      <div className="w-12 h-12 rounded-full bg-white/20 dark:bg-white/10 flex items-center justify-center">
                        <Building2 className={`h-7 w-7 ${
                          !isIdentityVerified ? 'text-muted-foreground' : (hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0 ? 'text-white' : 'text-purple-600 dark:text-purple-300')
                        }`} />
                      </div>
                    </div>
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                      !isIdentityVerified
                        ? 'bg-muted-foreground/40'
                        : hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'bg-purple-500 dark:bg-purple-600'
                        : 'bg-purple-300 dark:bg-purple-700'
                    }`}>
                      <span className="text-xs font-black text-white">3</span>
                    </div>
                  </div>
                  
                  {/* Phase Label */}
                  <div className="text-center mb-6">
                    <p className={`text-sm font-bold uppercase tracking-wider mb-1 ${
                      hasStartedProcess && identificationAccess.isComplete && exchangeDaysRemaining !== null && exchangeDaysRemaining >= 0
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-purple-400 dark:text-purple-500'
                    }`}>Phase 3</p>
                    <p className="text-xs text-muted-foreground">Acquisition</p>
                  </div>
                  
                  {/* Card */}
                  <div className="relative w-full py-3 rounded-xl bg-card border-2 border-purple-200/50 dark:border-purple-800/30 transition-all duration-300 flex flex-col">
                    <div className="flex flex-col items-center text-center flex-1">
                      <h3 className="text-sm font-bold text-foreground mb-0.5">
                        180-Day Exchange
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        Complete exchange
                      </p>
                      {salesAccess.isComplete && exchangeDeadline && (
                        <div className="flex items-center justify-center space-x-1 text-xs text-muted-foreground mb-2">
                          <Calendar className="h-3 w-3" />
                          <span className="font-medium">{formatDate(exchangeDeadline)}</span>
                        </div>
                      )}
                      {!isIdentityVerified ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      ) : purchaseAccess.isSubmitted && disbursementAccess.isSubmitted ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold">
                          <CheckCircle className="h-2.5 w-2.5 mr-1" />
                          Completed
                        </div>
                      ) : purchaseAccess.canAccess && purchaseAccess.completionPercent > 0 ? (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold">
                          <Clock className="h-2.5 w-2.5 mr-1" />
                          In Progress
                        </div>
                      ) : salesAccess.isComplete && exchangeDeadline && exchangeDaysRemaining !== null && exchangeDaysRemaining > 0 ? (
                        <div className="inline-flex flex-col items-center px-3 py-2 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/70 dark:to-purple-800/70 border-2 border-purple-300 dark:border-purple-600 shadow-md">
                          <div className="text-3xl font-black leading-none text-purple-700 dark:text-purple-100 tracking-tight">
                            {exchangeDaysRemaining}
                          </div>
                          <div className="text-xs font-bold mt-1 text-purple-800 dark:text-purple-200 uppercase tracking-wider">
                            days to close
                          </div>
                        </div>
                      ) : (
                        <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                          Not Started
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          </div>

          {/* Warning Messages */}
          {exchangeDeadline && exchangeDaysRemaining !== null && exchangeDaysRemaining > 0 && exchangeDaysRemaining <= 14 && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-red-900 dark:text-red-200">Exchange Deadline Critical</h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  Only {exchangeDaysRemaining} days left to complete your exchange.
                  Final deadline: {formatDate(exchangeDeadline)}
                </p>
              </div>
            </div>
          )}

          {!effectiveSaleDateForDisplay && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200">Exchange Not Started</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Start your 1031 exchange process to begin tracking your timeline. The 180-day exchange period will begin once you enter your property sale date.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Exchange Hubs Section */}
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Exchange Hubs
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
            {/* Phase 1 */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/40 border-2 border-green-300 dark:border-green-700">
                  <span className="text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider">Phase 1</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Initial Setup</p>
              </div>
              <HubCard
                title="Sales Hub"
                subtitle="Relinquished property details"
                icon={<Layers className="h-4 w-4 text-green-600 dark:text-green-400" />}
                color="green"
                completionPercent={salesAccess.completionPercent}
                isComplete={salesAccess.isComplete}
                canAccess={salesAccess.canAccess}
                lockedMessage={salesAccess.message || 'Identity verification required'}
                isNextStep={nextStep?.hub === 'sales'}
                isLocked={isHubLocked('sales')}
                onToggleLock={salesAccess.isSubmitted ? undefined : () => toggleHubLock('sales')}
                onClick={() => {
                  if (!salesAccess.canAccess) {
                    setShowKYCModal(true)
                  } else {
                    setWizardInitialTab('initial')
                    setIsWizardOpen(true)
                  }
                }}
              />
            </div>

            {/* Phase 2 — Identification + Escrow */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  {identificationAccess.isComplete && <CheckCircle2 className="h-3 w-3 text-blue-600 dark:text-blue-400" />}
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Phase 2</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">During Exchange</p>
              </div>
              <HubCard
                title="Identification Hub"
                subtitle="Identify replacement properties"
                icon={<FileCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                color="blue"
                completionPercent={identificationAccess.completionPercent}
                isComplete={identificationAccess.isComplete}
                canAccess={identificationAccess.canAccess}
                lockedMessage={identificationAccess.message}
                isNextStep={nextStep?.hub === 'identification'}
                isLocked={isHubLocked('identification')}
                onToggleLock={identificationAccess.isSubmitted ? undefined : () => toggleHubLock('identification')}
                onClick={() => setIsExchangeHubOpen(true)}
              />
              <HubCard
                title="Escrow Hub"
                subtitle="QI custody acknowledgment"
                icon={<Landmark className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />}
                color="cyan"
                completionPercent={escrowAccess.completionPercent}
                isComplete={escrowAccess.isComplete}
                canAccess={escrowAccess.canAccess}
                lockedMessage={escrowAccess.message}
                isNextStep={nextStep?.hub === 'escrow'}
                isLocked={isHubLocked('escrow')}
                onToggleLock={escrowAccess.isSubmitted ? undefined : () => toggleHubLock('escrow')}
                onClick={() => setIsEscrowHubOpen(true)}
              />
            </div>

            {/* Phase 3 */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                  {purchaseAccess.isComplete && disbursementAccess.isComplete && <CheckCircle2 className="h-3 w-3 text-purple-500 dark:text-purple-400" />}
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-500 dark:text-purple-400">Phase 3</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Acquisition</p>
              </div>
              <HubCard
                title="Purchase Hub"
                subtitle="Replacement property purchase"
                icon={<Building2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />}
                color="purple"
                completionPercent={purchaseAccess.completionPercent}
                isComplete={purchaseAccess.isComplete}
                canAccess={purchaseAccess.canAccess}
                lockedMessage={purchaseAccess.message}
                isNextStep={nextStep?.hub === 'purchase'}
                isLocked={isHubLocked('purchase')}
                onToggleLock={purchaseAccess.isSubmitted ? undefined : () => toggleHubLock('purchase')}
                onClick={() => setIsPurchaseHubOpen(true)}
              />
              <HubCard
                title="Disbursement Hub"
                subtitle="Wire transfer details"
                icon={<DollarSign className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                color="orange"
                completionPercent={disbursementAccess.completionPercent}
                isComplete={disbursementAccess.isComplete}
                canAccess={disbursementAccess.canAccess}
                lockedMessage={disbursementAccess.message}
                isNextStep={nextStep?.hub === 'disbursement'}
                isLocked={isHubLocked('disbursement')}
                onToggleLock={disbursementAccess.isSubmitted ? undefined : () => toggleHubLock('disbursement')}
                onClick={() => setIsDisbursementHubOpen(true)}
              />
            </div>

            {/* POST — IRS Reporting & Wrap-Up */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full border-2 bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800">
                  {irsAccess.isComplete && <CheckCircle2 className="h-3 w-3 text-cyan-600 dark:text-cyan-400" />}
                  <span className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">POST</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">IRS Reporting & Wrap-Up</p>
              </div>
              <HubCard
                title="IRS Hub"
                subtitle="Tax reporting and forms"
                icon={<FileText className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />}
                color="cyan"
                completionPercent={irsAccess.completionPercent}
                isComplete={irsAccess.isComplete}
                canAccess={irsAccess.canAccess}
                lockedMessage={irsAccess.message}
                isNextStep={nextStep?.hub === 'irs'}
                isLocked={isHubLocked('irs')}
                onToggleLock={irsAccess.isSubmitted ? undefined : () => toggleHubLock('irs')}
                onClick={() => setIsIrsHubOpen(true)}
              />
            </div>
          </div>
        </Card>

      <div className="grid grid-cols-1 gap-6">
        {/* Recent Activity */}
        {user && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
              <Activity className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Recent Activity
            </h2>
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {activity.status === 'completed' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">{activity.timestamp}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No recent activity yet</p>
              )}
            </div>
            {allActivities.length > 10 && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setIsAllActivityModalOpen(true)
                    setCurrentPage(1)
                  }}
                >
                  <Activity className="h-4 w-4 mr-2" />
                  View All Activity ({allActivities.length})
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* All Activity Modal */}
      {isAllActivityModalOpen && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsAllActivityModalOpen(false)
            }
          }}
        >
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col my-4">
            {/* Header */}
            <div className="border-b border-border px-6 py-4 flex items-center justify-between bg-gradient-to-r from-primary/10 to-transparent">
              <div>
                <h2 className="text-xl font-semibold text-foreground">All Activity</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {allActivities.length} total {allActivities.length === 1 ? 'activity' : 'activities'}
                </p>
              </div>
              <button 
                onClick={() => setIsAllActivityModalOpen(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Activities List */}
            <div className="flex-1 overflow-y-auto p-6">
              {paginatedActivities.length > 0 ? (
                <div className="space-y-4">
                  {paginatedActivities.map((activity) => (
                    <div 
                      key={activity.id} 
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {activity.status === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{activity.title}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{activity.description}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No activities found</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="border-t border-border px-6 py-4 bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, allActivities.length)} of {allActivities.length} activities
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center space-x-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous</span>
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number
                        if (totalPages <= 5) {
                          pageNum = i + 1
                        } else if (currentPage <= 3) {
                          pageNum = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i
                        } else {
                          pageNum = currentPage - 2 + i
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                              currentPage === pageNum
                                ? 'bg-primary text-primary-foreground shadow-md'
                                : 'bg-card text-foreground hover:bg-muted'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center space-x-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-on Products Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Additional Services
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enhance your 1031 exchange experience with our partner services
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-border rounded-lg hover:border-primary transition-colors cursor-pointer group">
            <div className="flex items-start space-x-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Tax Preparation
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Partner with TurboTax for seamless tax filing. Get expert help with your 1031 exchange tax forms.
                </p>
                <span className="text-xs text-primary font-medium">
                  Coming Soon →
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 border border-border rounded-lg hover:border-primary transition-colors cursor-pointer group">
            <div className="flex items-start space-x-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Calculator className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Cost Segregation
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  Maximize your tax benefits with professional cost segregation studies for your replacement property.
                </p>
                <span className="text-xs text-primary font-medium">
                  Coming Soon →
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>


      {/* Wizard Hub Modal */}
      {isWizardOpen && (
        <ExchangeWizardHub 
          initialTab={wizardInitialTab}
          onClose={() => setIsWizardOpen(false)}
        />
      )}

      {/* Escrow Hub Modal */}
      {isEscrowHubOpen && (
        <EscrowHub
          onClose={() => setIsEscrowHubOpen(false)}
        />
      )}

      {/* Exchange Hub Modal */}
      {isExchangeHubOpen && (
        <ExchangeHub
          onClose={() => setIsExchangeHubOpen(false)}
        />
      )}

      {/* Acquisition Hub Modal */}
      {isPurchaseHubOpen && (
        <PurchaseHub 
          onClose={() => setIsPurchaseHubOpen(false)}
        />
      )}

      {isDisbursementHubOpen && (
        <DisbursementHub
          onClose={() => setIsDisbursementHubOpen(false)}
        />
      )}

      {isIrsHubOpen && (
        <IrsHub
          onClose={() => setIsIrsHubOpen(false)}
          onSectionComplete={() => window.dispatchEvent(new CustomEvent('wizardDataUpdated'))}
        />
      )}

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
        onComplete={() => {
          setShowKYCModal(false)
          if (user?.email && String(user.email).toLowerCase().trim() === 'demo1@open1031.ai') {
            setWizardInitialTab('initial')
            setIsWizardOpen(true)
            sessionStorage.setItem('sales_hub_shown_demo1_session', 'true')
            demoGuidanceStageRef.current = ''
            updateDemoGuidanceAlert()
          }
        }}
      />
      
    </div>
  )
}
